// AmbientSea — cosmetic ships, wakes and gulls (docs/sea-design.md 5.1).
//
// Perf (renderer-crash-rca rules): THREE draw calls total — one InstancedMesh
// of merged cog geometry (<= MAX_SHIPS instances), one of the GPU-animated foam
// wake (the same instances, whose tufts move entirely in the shader), and one
// of merged gull geometry (<= MAX_GULLS). Buffers are allocated once at the
// caps; instance attributes are rewritten only when the plan (terrain
// signature) changes. Per frame: <= 48 matrix writes through a preallocated
// Object3D (no allocation; the wake shares the ship's matrix); gulls and wake
// tufts cost nothing on the CPU — their motion runs in the shader. Honours
// prefers-reduced-motion (clock frozen, ships parked, wake hidden) and the
// particleEffects setting (clock frozen, gulls and wake hidden) exactly like
// Waterfalls.

import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useSettings } from '../../settings/useSettings.js';
import { dayNightState, nightFactor } from '../DayNightLighting.js';
import { prefersReducedMotion } from '../Waterfalls.js';
import {
  MAX_GULLS,
  MAX_SHIPS,
  PIRATE_PALETTE,
  SHIP_PALETTE,
  type SamplePoint,
  type TrafficPlan,
  sampleRoute,
} from './seaRoutes.js';
import { buildGullGeometry, buildShipGeometry } from './trafficGeometry.js';
import { trafficMaterial, trafficNight, trafficTime } from './trafficMaterial.js';
import { buildWakeGeometry } from './wakeGeometry.js';
import { wakeMaterial } from './wakeMaterial.js';

// Module-level singletons: geometry + per-instance tint buffers live for the page.
const shipGeo = buildShipGeometry();
const gullGeo = buildGullGeometry();
const wakeGeo = buildWakeGeometry();
const shipTint = new THREE.InstancedBufferAttribute(new Float32Array(MAX_SHIPS * 4), 4);
const gullTint = new THREE.InstancedBufferAttribute(new Float32Array(MAX_GULLS * 4), 4);
// Per-instance rig (1/2/3 masts). The geometry carries every rig; the shader
// collapses the ones this instance does not use.
const shipRig = new THREE.InstancedBufferAttribute(new Float32Array(MAX_SHIPS), 1);
// 1 = pirate. Drives the near-full sail tint (black reads black) and the flag.
const shipPirate = new THREE.InstancedBufferAttribute(new Float32Array(MAX_SHIPS), 1);
shipGeo.setAttribute('aTint', shipTint);
shipGeo.setAttribute('aVariant', shipRig);
shipGeo.setAttribute('aPirate', shipPirate);
// The gulls share the material's program, so they carry the same attributes
// (all zero: `aShipVariant` is 0 on every gull vertex, so nothing collapses).
gullGeo.setAttribute(
  'aVariant',
  new THREE.InstancedBufferAttribute(new Float32Array(MAX_GULLS), 1),
);
// The wake shares the ships' tint buffer: it only reads the phase (`.w`), which
// de-synchronises the wakes, and one upload serves both meshes.
wakeGeo.setAttribute('aTint', shipTint);
gullGeo.setAttribute('aTint', gullTint);

const merchantPalette = SHIP_PALETTE.map((c) => new THREE.Color(c));
const piratePalette = PIRATE_PALETTE.map((c) => new THREE.Color(c));
const _obj = new THREE.Object3D();
const _pt: SamplePoint = { x: 0, y: 0, z: 0, dx: 1, dz: 0 };

function writeShips(
  plan: TrafficPlan,
  ships: THREE.InstancedMesh,
  wakes: THREE.InstancedMesh,
  t: number,
): void {
  const n = Math.min(plan.ships.length, MAX_SHIPS);
  for (let i = 0; i < n; i++) {
    const s = plan.ships[i]!;
    sampleRoute(plan.routes[s.route]!, s.start + s.speed * t, _pt);
    // Moving backwards along the route turns the bow around.
    const dir = s.speed < 0 ? -1 : 1;
    _obj.position.set(_pt.x, _pt.y, _pt.z);
    _obj.rotation.set(0, Math.atan2(-_pt.dz * dir, _pt.dx * dir), 0);
    // Size is a pure scale: the wake and the bob ride along with it.
    _obj.scale.setScalar(s.size);
    _obj.updateMatrix();
    ships.setMatrixAt(i, _obj.matrix);
    // The foam field is authored in the ship's own frame, so it takes the same
    // matrix: the trail turns and travels with the hull.
    wakes.setMatrixAt(i, _obj.matrix);
  }
  ships.count = n;
  wakes.count = n;
  ships.instanceMatrix.needsUpdate = true;
  wakes.instanceMatrix.needsUpdate = true;
}

/** Rewrite instance data that only changes with the plan. */
function writePlan(
  plan: TrafficPlan,
  ships: THREE.InstancedMesh,
  wakes: THREE.InstancedMesh,
  gulls: THREE.InstancedMesh,
) {
  const ns = Math.min(plan.ships.length, MAX_SHIPS);
  for (let i = 0; i < ns; i++) {
    const ship = plan.ships[i]!;
    // Pirates carry tarred/dark sails; merchants keep the pale canvas.
    const pal = ship.role === 'pirate' ? piratePalette : merchantPalette;
    const c = pal[ship.tint % pal.length]!;
    shipTint.setXYZW(i, c.r, c.g, c.b, ship.phase);
    shipRig.setX(i, ship.variant);
    shipPirate.setX(i, ship.role === 'pirate' ? 1 : 0);
  }
  shipTint.needsUpdate = true;
  shipRig.needsUpdate = true;
  shipPirate.needsUpdate = true;
  const ng = Math.min(plan.gulls.length, MAX_GULLS);
  _obj.rotation.set(0, 0, 0);
  _obj.scale.setScalar(1);
  for (let i = 0; i < ng; i++) {
    const g = plan.gulls[i]!;
    gullTint.setXYZW(i, g.radius, g.omega, g.angle0, g.phase);
    _obj.position.set(g.x, g.y, g.z);
    _obj.updateMatrix();
    gulls.setMatrixAt(i, _obj.matrix);
  }
  gullTint.needsUpdate = true;
  gulls.count = ng;
  gulls.instanceMatrix.needsUpdate = true;
  writeShips(plan, ships, wakes, trafficTime.value);
}

export function AmbientSea({ plan }: { readonly plan: TrafficPlan }) {
  const particleEffects = useSettings((s) => s.particleEffects);
  const reduced = useMemo(prefersReducedMotion, []);
  const shipRef = useRef<THREE.InstancedMesh>(null);
  const wakeRef = useRef<THREE.InstancedMesh>(null);
  const gullRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    if (shipRef.current && wakeRef.current && gullRef.current) {
      writePlan(plan, shipRef.current, wakeRef.current, gullRef.current);
    }
  }, [plan]);

  useFrame((_, delta) => {
    trafficNight.value = nightFactor(dayNightState.sunHeight);
    if (reduced || !particleEffects || plan.ships.length === 0) return;
    trafficTime.value += Math.min(delta, 0.1);
    if (shipRef.current && wakeRef.current) {
      writeShips(plan, shipRef.current, wakeRef.current, trafficTime.value);
    }
  });

  if (plan.ships.length === 0 && plan.gulls.length === 0) return null;
  return (
    <group name="ambient-sea">
      <instancedMesh
        ref={shipRef}
        name="sea-ships"
        args={[shipGeo, trafficMaterial, MAX_SHIPS]}
        frustumCulled={false}
        raycast={() => null}
        renderOrder={3}
      />
      <instancedMesh
        ref={wakeRef}
        name="sea-wake"
        args={[wakeGeo, wakeMaterial, MAX_SHIPS]}
        frustumCulled={false}
        raycast={() => null}
        renderOrder={3}
        // The wake is pure motion: with ambient motion reduced or particle
        // effects off the clock is frozen, and a frozen foam field reads as
        // debris rather than as a wake.
        visible={!reduced && particleEffects}
      />
      <instancedMesh
        ref={gullRef}
        name="sea-gulls"
        args={[gullGeo, trafficMaterial, MAX_GULLS]}
        frustumCulled={false}
        raycast={() => null}
        renderOrder={3}
        visible={particleEffects}
      />
    </group>
  );
}
